import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Evaluation, Task, TaskResult } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import { DetailSection, GroupedContent } from '@/shared/ui/layout';
import { calculateCategoryScore } from '@/shared/utils/scoreCalculation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './styles.css';

// Helper function to group results by category
const groupResultsByCategory = (results: TaskResult[]) => {
  const grouped: Record<string, TaskResult[]> = {};
  
  results.forEach(result => {
    const task = result.task as Task;
    const category = task.category || 'Uncategorized';
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(result);
  });
  
  // Sort categories alphabetically
  return Object.keys(grouped)
    .sort()
    .reduce((acc, category) => {
      acc[category] = grouped[category];
      return acc;
    }, {} as Record<string, TaskResult[]>);
};

// Helper function to get score status
const getScoreStatus = (result: TaskResult, task: Task) => {
  if (result.score === null) return 'not-evaluated';
  
  if (typeof result.score === 'boolean') {
    return result.score ? 'full-score' : 'zero-score';
  }
  
  if (task.scoringMethod === 'numerical' && task.scoringScale) {
    const { min, max } = task.scoringScale;
    const score = result.score as number;
    const percentage = (score - min) / (max - min);
    
    if (percentage >= 0.8) return 'full-score';
    if (percentage <= 0.2) return 'zero-score';
    return 'partial-score';
  }
  
  return 'not-evaluated';
};

const EvaluationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [groupedResults, setGroupedResults] = useState<Record<string, TaskResult[]>>({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const data = await api.evaluations.getById(id);
      setEvaluation(data);
      
      // Group results by category
      setGroupedResults(groupResultsByCategory(data.results));
      
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to fetch evaluation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleDelete = async () => {
    if (!evaluation?._id || !window.confirm('Are you sure you want to delete this evaluation?')) return;

    try {
      await api.evaluations.delete(evaluation._id);
      toast.success('Evaluation deleted successfully!');
      navigate('/evaluations');
    } catch (err) {
      toast.error((err as ApiError).message || 'Failed to delete evaluation');
    }
  };

  if (loading) return <div className="loading">Loading evaluation details...</div>;
  if (!evaluation) return <div>Evaluation not found</div>;

  const actions = (
    <div className="actions-group">
      {evaluation.status === 'completed' && (
        <Link 
          to={`/tests/${typeof evaluation.test === 'string' ? evaluation.test : evaluation.test._id}/evaluate/${evaluation._id}`} 
          className="button primary"
        >
          Correct
        </Link>
      )}
      <button onClick={handleDelete} className="button danger">
        Delete
      </button>
      <Link to="/evaluations" className="button">
        Back
      </Link>
    </div>
  );

  // Calculate total score for display in details
  const calculateTotalScore = () => {
    if (!evaluation.results.length) return "0/0 (0%)";
    
    const scoreResult = calculateCategoryScore(evaluation.results);
    return scoreResult.displayText;
  };

  const testName = typeof evaluation.test === 'string' ? evaluation.test : evaluation.test.name;
  const pageTitle = `Evaluation of ${evaluation.modelName} on ${testName}`;

  const detailItems = [
    {
      label: 'Status:',
      value: <span className={`status ${evaluation.status}`}>{evaluation.status}</span>
    },
    {
      label: 'Test:',
      value: testName
    },
    {
      label: 'Tasks Evaluated:',
      value: evaluation.results.length
    },
    {
      label: 'Categories:',
      value: Object.keys(groupedResults).length
    },
    {
      label: 'Total Score:',
      value: calculateTotalScore()
    },
    {
      label: 'Completed:',
      value: new Date(evaluation.completedAt!).toLocaleString()
    }
  ];

  const renderResultGroup = (category: string, results: TaskResult[]) => (
    <div className="results-list">
      {results.map((result) => {
        const task = result.task as Task;
        const scoreStatus = getScoreStatus(result, task);
        
        // Format the score display
        let scoreDisplay;
        if (result.score === null) {
          scoreDisplay = 'Not evaluated';
        } else if (typeof result.score === 'boolean') {
          scoreDisplay = result.score ? 'Pass' : 'Fail';
        } else if (task.scoringMethod === 'numerical' && task.scoringScale) {
          scoreDisplay = `${result.score}/${task.scoringScale.max} (${Math.round((result.score / task.scoringScale.max) * 100)}%)`;
        } else {
          scoreDisplay = String(result.score);
        }
        
        return (
          <div 
            key={task._id} 
            className={`task-evaluation-card ${scoreStatus}`}
            onClick={() => navigate(`/tasks/${task._id}`)}
          >
            <h3 className="task-title">{task.title}</h3>
            
            <div className="task-content">
              <div className="content-box" onClick={(e) => e.stopPropagation()}>
                <div className="text-content">
                  {task.content}
                </div>
              </div>
              
              {/* Display task attachments if any */}
              {task.attachments && task.attachments.length > 0 && (
                <div className="task-attachments">
                  <div className="attachments-list">
                    {task.attachments.map((attachment) => {
                      const isImage = attachment.mimetype.startsWith('image/');
                      const isPdf = attachment.mimetype === 'application/pdf';
                      const downloadUrl = api.tasks.getAttachmentUrl(task._id!, attachment._id!);
                      
                      // Function to handle drag start
                      const handleDragStart = (e: React.DragEvent) => {
                        e.stopPropagation(); // Prevent card click when dragging
                        // Set the drag data with the download URL and filename
                        e.dataTransfer.setData('text/uri-list', downloadUrl);
                        e.dataTransfer.setData('text/plain', downloadUrl);
                        e.dataTransfer.setData('DownloadURL', `${attachment.mimetype}:${attachment.originalname}:${downloadUrl}`);
                        // Set the drag effect to 'copy' to indicate a file copy operation
                        e.dataTransfer.effectAllowed = 'copy';
                      };
                      
                      return (
                        <div 
                          key={attachment._id} 
                          className="attachment-item"
                          draggable="true"
                          onDragStart={handleDragStart}
                          onClick={(e) => e.stopPropagation()} // Prevent card click when clicking attachment
                          title="Drag to desktop or other applications"
                        >
                          {isImage ? (
                            <div className="image-preview">
                              <img src={downloadUrl} alt={attachment.originalname} />
                            </div>
                          ) : (
                            <div className="file-icon">
                              {isPdf ? '📄 PDF' : '📄'}
                            </div>
                          )}
                          <div className="attachment-info">
                            <span className="attachment-name">{attachment.originalname}</span>
                            <a 
                              href={downloadUrl} 
                              download={attachment.originalname}
                              className="download-link"
                              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking download
                            >
                              Download
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="task-footer">
              <div className="task-meta">
                <div className="score-display">
                  <span className="score-label">Score:</span>
                  <span className={`score-value ${scoreStatus}`}>{scoreDisplay}</span>
                  {task.scoringMethod === 'numerical' && task.scoringScale && (
                    <span className="scale-info">
                      (Scale: {task.scoringScale.min} - {task.scoringScale.max})
                    </span>
                  )}
                </div>
                {result.notes && (
                  <div 
                    className="notes-preview"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="notes-label">Notes:</span>
                    <div className="notes-content markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                        components={{
                          code({ node, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            // The `inline` property is not available in the latest versions,
                            // so we check for the presence of a language class to identify fenced code blocks.
                            return match ? (
                              <SyntaxHighlighter
                                style={materialDark as any}
                                language={match[1]}
                                PreTag="div"
                              >
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code className={className} {...props}>
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {result.notes}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Custom title renderer for the GroupedContent component
  const renderCategoryTitle = (category: string) => {
    // Calculate category score using the utility function
    const scoreResult = calculateCategoryScore(groupedResults[category]);
    
    return (
      <div className="category-title-wrapper">
        <span>{category}</span>
        <span className="category-score">{scoreResult.displayText}</span>
      </div>
    );
  };

  return (
    <PageLayout
      title={pageTitle}
      actions={actions}
    >
      <div className="evaluation-content">
        <DetailSection items={detailItems} />

        <h3>Results by Category</h3>
        <div className="evaluation-results">
          {Object.keys(groupedResults).length === 0 ? (
            <div className="no-results">No results found.</div>
          ) : (
            <GroupedContent
              groups={groupedResults}
              renderGroup={renderResultGroup}
              groupTitle={renderCategoryTitle}
              emptyMessage="No results found."
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default EvaluationDetail; 